/**
 * DOM helpers every primitive shares: finding the parts of a root by
 * `data-slot`, resolving `asChild` wrappers, and writing `data-state`.
 *
 * A part belongs to a root only when no other root of the same kind sits
 * between them, so nested dialogs, menus and submenus each find their own
 * parts and never their neighbours'.
 */
/** True when `part` belongs to `root`, not to a nested root with the same slot. */
export function ownedBy(root, name, part) {
    return part.closest(`[data-slot="${name}"]`) === root;
}
/** Every part of `root` with `data-slot="<slot>"` that `root` owns. */
export function partsOf(root, rootName, slot) {
    return Array.from(root.querySelectorAll(`[data-slot="${slot}"]`)).filter((part) => ownedBy(root, rootName, part));
}
/** The first owned part with this slot, or null. */
export function partOf(root, rootName, slot) {
    return partsOf(root, rootName, slot)[0] ?? null;
}
/**
 * The element that carries behaviour for a part.
 *
 * A part written `<span data-slot="x-trigger" data-as-child style="display: contents">`
 * is a transparent wrapper (shadcn's `asChild`): ARIA, state and listeners go on
 * its first element child, which is the real focusable control.
 */
export function partTarget(part) {
    if (!part.hasAttribute('data-as-child'))
        return part;
    return part.firstElementChild instanceof HTMLElement ? part.firstElementChild : part;
}
/** Writes `data-state` on every given element. */
export function setState(state, ...elements) {
    for (const element of elements)
        element?.setAttribute('data-state', state);
}
